import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '@/monitoring/ga4';

const MILESTONES = [25, 50, 75, 90] as const;
const THROTTLE_MS = 200;

/**
 * 현재 페이지의 스크롤 깊이(25/50/75/90%)를 GA4에 전송한다.
 * 라우트가 변경되면 마일스톤을 리셋한다.
 */
export function useScrollDepthTracking(): void {
  const location = useLocation();
  const firedRef = useRef<Set<number>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    firedRef.current.clear();
  }, [location.pathname]);

  useEffect(() => {
    const measure = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const percent = Math.round((window.scrollY / docHeight) * 100);

      for (const milestone of MILESTONES) {
        if (percent >= milestone && !firedRef.current.has(milestone)) {
          firedRef.current.add(milestone);
          trackEvent('scroll_depth', {
            scroll_depth_percent: milestone,
            page_path: location.pathname,
          });
        }
      }
    };

    const handler = () => {
      if (timerRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        measure();
      }, THROTTLE_MS);
    };

    window.addEventListener('scroll', handler, { passive: true });
    return () => {
      window.removeEventListener('scroll', handler);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [location.pathname]);
}
